import type { AppDispatch } from '@/app/store/store';
import { API_TAGS, type ApiTag } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { SOCKET_INVALIDATION_DEBOUNCE_MS } from '@/shared/socket/waha/config';
import {
  extractSocketEventFromBody,
  extractSocketPayloadContext,
} from '@/shared/socket/event-router';
import type { SocketEventName } from '@/shared/socket/types';

const TABLE_ROOM_EVENTS = new Set<SocketEventName>([
  'TABLE_CREATE',
  'TABLE_UPDATE',
  'TABLE_DELETE',
  'ROOM_REFRESH',
]);

type CacheTag = { type: ApiTag; id: string };

export type RealtimeEventInput = {
  dispatch: AppDispatch;
  parsedBody: unknown;
  eventName?: SocketEventName | null;
};

class RealtimeEventProcessor {
  private invalidateTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingTags: CacheTag[] = [];

  reset() {
    this.clearInvalidateTimer();
    this.pendingTags = [];
  }

  enqueueOrProcess(input: RealtimeEventInput) {
    this.process(input);
  }

  private process({
    dispatch,
    parsedBody,
    eventName: eventNameInput,
  }: RealtimeEventInput) {
    try {
      const eventName =
        eventNameInput ?? extractSocketEventFromBody(parsedBody);
      const context = extractSocketPayloadContext(parsedBody);
      const branchCode = context.branchCode?.trim();

      if (!branchCode) {
        return;
      }

      const tags = this.buildInvalidationTags({
        branchCode,
        bookingCode: context.bookingCode,
        eventName,
      });

      this.scheduleInvalidate(dispatch, tags);
    } catch (error) {
      console.error('[waha-socket] message handling failed', error);
    }
  }

  private buildInvalidationTags({
    branchCode,
    bookingCode,
    eventName,
  }: {
    branchCode: string;
    bookingCode?: string;
    eventName: SocketEventName | null;
  }): CacheTag[] {
    const tags: CacheTag[] = [
      { type: API_TAGS.Bookings, id: branchCode },
      { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
    ];

    const normalizedBookingCode = bookingCode?.trim();
    if (normalizedBookingCode) {
      tags.push({ type: API_TAGS.Bookings, id: normalizedBookingCode });
    }

    if (eventName === 'UPDATE_AMENITY_ORDER') {
      tags.push({ type: API_TAGS.Amenities, id: branchCode });
    }

    if (eventName && TABLE_ROOM_EVENTS.has(eventName)) {
      tags.push({ type: API_TAGS.Rooms, id: branchCode });
    }

    return tags;
  }

  private scheduleInvalidate(dispatch: AppDispatch, tags: CacheTag[]) {
    const existing = new Set(
      this.pendingTags.map((tag) => `${tag.type}:${tag.id}`),
    );

    tags.forEach((tag) => {
      const key = `${tag.type}:${tag.id}`;
      if (!existing.has(key)) {
        existing.add(key);
        this.pendingTags.push(tag);
      }
    });

    this.clearInvalidateTimer();
    this.invalidateTimer = setTimeout(() => {
      this.invalidateTimer = null;

      const tagDescriptors = this.pendingTags;
      this.pendingTags = [];

      if (tagDescriptors.length > 0) {
        dispatch(baseApi.util.invalidateTags(tagDescriptors));
      }
    }, SOCKET_INVALIDATION_DEBOUNCE_MS);
  }

  private clearInvalidateTimer() {
    if (this.invalidateTimer) {
      clearTimeout(this.invalidateTimer);
      this.invalidateTimer = null;
    }
  }
}

export const realtimeEventProcessor = new RealtimeEventProcessor();
